// src/components/NotificationCentre.js
//
// Track 6 item 6a-10 (2026-09-14, task list section 227). src\data\notifications.js has produced
// notification RECORDS since 2026-08-31 (ruling 13's two-recipient follow-up, ruling 89's assistance
// request) and the rules for /notifications have held their read shape since - and until this cycle
// no screen read one. A follow-up nobody can see is the failure SS3.1 of the workspace requirements
// names. This is the centre: in-app, behind the same records an e-mail adapter would later read.
//
// The read is shaped the way the rule can prove it (firestore.rules match /notifications):
//   claimant   audience == 'claimant'      && recipientUid == me
//   GC         audience == 'general_chair' && committee == mine
//   LC         audience == 'local_chair'   && scopeKey in my scopes   (one query per scope)
//   person     audience == 'office_holder' && recipientUid == me   (S7: a vacancy's notices, section 245)
//   admin      unshaped
// Ordering, unread-first, is the module's (orderForCentre); the only write is readAt, on my own.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { orderForCentre, unreadCount, isUnread, stampText, mayMarkRead } from '../data/notifications';   // stampText: Record 592 (F14); mayMarkRead: forensic audit 2026-10-16
import RulingRef from './RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import { capScopes } from '../data/scopeCap';   // T5 item 20: the 30-territory cap, in one place
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

function queriesFor(user) {
  const base = collection(db, 'notifications');
  if (!user || !user.uid) return [];
  if (user.isAdmin) return [query(base)];
  const qs = [
    query(base, where('audience', '==', 'claimant'), where('recipientUid', '==', user.uid)),
    query(base, where('audience', '==', 'office_holder'), where('recipientUid', '==', user.uid)),   // S7 (section 245): a vacancy's notice to me by name
  ];
  if (user.role === 'general_chair' && user.committee) qs.push(query(base, where('audience', '==', 'general_chair'), where('committee', '==', user.committee)));
  if (user.role === 'local_chair' && Array.isArray(user.scopes)) capScopes(user).scopes.forEach(s => qs.push(query(base, where('audience', '==', 'local_chair'), where('scopeKey', '==', s))));
  return qs;
}

export default function NotificationCentre({ user, onViewClaim, compact }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(!compact);
  const key = (user && user.uid) + '|' + (user && user.role) + '|' + (user && user.committee) + '|' + ((user && user.scopes) || []).join(',');
  useEffect(() => {
    const qs = queriesFor(user);
    if (!qs.length) { setRows([]); return undefined; }
    const per = qs.map(() => null);
    const unsubs = qs.map((q, i) => onSnapshot(q, snap => {
      per[i] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (per.every(x => x !== null)) { const m = new Map(); per.flat().forEach(r => m.set(r.id, r)); setRows(Array.from(m.values())); setErr(''); }
    }, e => { setErr('Notifications could not be read: ' + (e && e.code ? e.code : e.message)); setRows([]); }));
    return () => unsubs.forEach(u => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const ordered = useMemo(() => orderForCentre(rows || []), [rows]);
  const unread = unreadCount(rows || []);
  const markRead = async (n) => {
    try { await updateDoc(doc(db, 'notifications', n.id), { readAt: new Date().toISOString() }); }
    catch (e) { setErr('Could not mark it read: ' + (e && e.code ? e.code : e.message)); }
  };
  const shown = open ? ordered : ordered.slice(0, 3);
  return (
    <div style={{ border: '1px solid ' + (unread ? C.accent : C.line), background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid ' + C.line, cursor: compact ? 'pointer' : 'default' }} {...activate(() => compact && setOpen(!open))}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Notifications</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>A rule added or annotated on your claim reaches you and your General Chairperson.<RulingRef user={user} n={13} why="A rule added or annotated on a claim is notified to the member and his General Chairperson." /> A member's request for help reaches his Local Chairperson.<RulingRef user={user} n={89} why="A member's request for help is notified to his Local Chairperson." /> A vacancy reaches its designated vice and the Secretary (S7). Nothing here is ever deleted.</div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: unread ? C.accent : C.ok, whiteSpace: 'nowrap' }}>{rows === null ? '…' : unread + ' unread'}{compact ? (open ? ' ▲' : ' ▼') : ''}</div>
      </div>
      <div style={{ padding: '8px 14px 12px', fontSize: 12, color: C.dim }}>
        {err ? <div style={{ color: C.bad }}>&#9888; {err}</div> : null}
        {rows === null && !err ? <div>Loading…</div> : null}
        {rows && rows.length === 0 && !err ? <div>Nothing addressed to you is on record.</div> : null}
        {shown.map(n => (
          <div key={n.id} style={{ borderTop: '1px solid ' + C.line, padding: '8px 0', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, marginTop: 5, background: isUnread(n) ? C.accent : C.line, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: C.text, fontWeight: isUnread(n) ? 700 : 400 }}>{n.title}</div>
              <div style={{ marginTop: 2 }}>{n.body}</div>
              <div style={{ fontSize: 11, marginTop: 3 }}>
                {n.actorName} ({String(n.actorRole || '').replace('_', ' ')}) · {stampText(n.createdAt).slice(0, 16).replace('T', ' ')}
                {n.claimId && onViewClaim ? <> · <button onClick={() => onViewClaim(n.claimId)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}>open the claim</button></> : null}
                {/* Forensic audit 2026-10-16: "mark read" only where the rule's update arms admit this account (never the
                    administrator on a notice addressed to someone else - the rule has no administrator arm). */}
                {isUnread(n) ? (mayMarkRead(n, user) ? <> · <button onClick={() => markRead(n)} style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}>mark read</button></> : <> · unread by its addressee</>) : <> · read {String(n.readAt || '').slice(0, 10)}</>}
              </div>
            </div>
          </div>
        ))}
        {!open && ordered.length > 3 ? <div style={{ marginTop: 6 }}><button onClick={() => setOpen(true)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline' }}>{ordered.length - 3} more</button></div> : null}
      </div>
    </div>
  );
}
